import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  Grid,
  Heading,
  IconButton,
  Box,
  Badge,
  Spinner,
  Center,
  Text,
} from "@chakra-ui/react";
import { X } from "lucide-react";
import { Procedures } from "@/types/procedures";
import { procedureService } from "@/lib/services/procedureService";

interface ViewProcedureModalProps {
  isOpen: boolean;
  onClose: () => void;
  procedureId: number | null;
}

const ViewProcedureModal: React.FC<ViewProcedureModalProps> = ({
  isOpen,
  onClose,
  procedureId,
}) => {
  const [procedure, setProcedure] = useState<Procedures | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Dummy ref to prevent auto focus
  const initialRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchProcedure = async () => {
      if (isOpen && procedureId) {
        setIsLoading(true);
        try {
          const data = await procedureService.getProcedure(procedureId);
          if (data) {
            setProcedure(data);
          } else {
            setProcedure(null);
          }
        } catch (error: any) {
          console.error("Failed to fetch procedure:", error);
          setProcedure(null);
        } finally {
          setIsLoading(false);
        }
      } else if (!isOpen) {
        setProcedure(null);
      }
    };

    fetchProcedure();
  }, [isOpen, procedureId]);

  // Get status badge color
  const getStatusColor = (status: string | number) => {
    if (status === "active" || status === 1) return "green";
    if (status === "inactive" || status === 0) return "red";
    return "gray";
  };

  // Get status text
  const getStatusText = (status: string | number) => {
    if (status === "active" || status === 1) return "Active";
    if (status === "inactive" || status === 0) return "Inactive";
    return String(status);
  };

  // Format price
  const formatPrice = (price: number) => {
    return `₱${price.toLocaleString("en-PH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  return (
    <>
      {isOpen && <div className="background-blur" />}

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        isCentered
        size="2xl"
        initialFocusRef={initialRef}
      >
        <ModalOverlay />
        <ModalContent borderRadius="0">
          <ModalHeader fontSize={{ base: "lg", md: "xl" }}>
            Procedure
            <IconButton
              aria-label="Close Modal"
              icon={<X />}
              onClick={onClose}
              position="absolute"
              right={5}
              top={5}
              size="sm"
              variant="link"
              color="#0A1B39"
              _hover={{ color: "#DC3611" }}
              _focus={{ boxShadow: "none" }}
            />
          </ModalHeader>
          <Box borderBottom="1px solid #E7E8EB" pb={2} mb={4}></Box>

          <ModalBody>
            {/* Dummy div for initial focus */}
            <div ref={initialRef} tabIndex={-1} />

            {isLoading ? (
              <Center py={8}>
                <Spinner size="lg" color="blue.500" />
              </Center>
            ) : !procedure ? (
              <Center py={8}>
                <Text color="gray.500">Procedure not found</Text>
              </Center>
            ) : (
              <>
                <Heading
                  size={{ base: "sm", md: "sm" }}
                  mb={4}
                  fontWeight="semibold"
                  color="#0A1B39"
                >
                  Procedure Information
                </Heading>

                <Grid
                  templateColumns={{ base: "1fr", sm: "repeat(3, 1fr)" }}
                  gap={4}
                >
                  <FormControl>
                    <FormLabel
                      fontSize={{ base: "xs", md: "sm" }}
                      fontWeight="400"
                      color="#0A1B39"
                    >
                      Procedure Name
                    </FormLabel>
                    <Input
                      value={procedure.name}
                      isReadOnly
                      size={{ base: "sm", md: "md" }}
                      bg="gray.50"
                      borderRadius="md"
                      borderColor="gray.200"
                      fontSize={{ base: "xs", md: "sm" }}
                      cursor="default"
                      _hover={{ borderColor: "gray.200" }}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel
                      fontSize={{ base: "xs", md: "sm" }}
                      fontWeight="400"
                      color="#0A1B39"
                    >
                      Cost
                    </FormLabel>
                    <Input
                      value={formatPrice(procedure.price)}
                      isReadOnly
                      bg="gray.50"
                      borderRadius="md"
                      borderColor="gray.200"
                      fontSize={{ base: "xs", md: "sm" }}
                      cursor="default"
                      _hover={{ borderColor: "gray.200" }}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel
                      fontSize={{ base: "xs", md: "sm" }}
                      fontWeight="400"
                      color="#0A1B39"
                    >
                      Status
                    </FormLabel>
                    <Box
                      bg="gray.50"
                      borderRadius="md"
                      borderWidth="1px"
                      borderColor="gray.200"
                      px={3}
                      py={2}
                      display="flex"
                      alignItems="center"
                      height={{ base: "32px", md: "40px" }}
                    >
                      {getStatusText(procedure.status!)}
                    </Box>
                  </FormControl>
                </Grid>

                <FormControl mt={4}>
                  <FormLabel
                    fontSize={{ base: "xs", md: "sm" }}
                    fontWeight="400"
                    color="#0A1B39"
                  >
                    Description
                  </FormLabel>
                  <Textarea
                    value={procedure.description || "No description provided"}
                    isReadOnly
                    bg="gray.50"
                    borderRadius="md"
                    borderColor="gray.200"
                    fontSize={{ base: "xs", md: "sm" }}
                    rows={4}
                    cursor="default"
                    _hover={{ borderColor: "gray.200" }}
                  />
                </FormControl>
              </>
            )}
          </ModalBody>

          <Box borderBottom="1px solid #E7E8EB" pb={2} mb={4}></Box>

          <ModalFooter gap={2} flexWrap="wrap">
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ViewProcedureModal;
